/**
 * Flyways Map (Migration routes) — ids, legend, camera helper.
 * SVG group ids must match `public/svg/flyways-map.svg`.
 * Tune cameras + motion in `@/config/flyway-map`.
 */

import {
  flywayMapCamera,
  flywayMapCameraMobile,
  flywayMapCameraMobileNarrowWidth,
  flywayMapCameraMobileWide,
  flywayMapCameraMobileWideWidth,
  flywayMapCameraRtl,
  type FlywayMapCamera,
} from '@/config/flyway-map';

import type { FlywayId } from '@/content/data/charts/flyways';

/** Text direction used to pick LTR vs RTL camera tables. */
export type FlywaysMapDir = 'ltr' | 'rtl';

/** Camera table used by `cameraToViewBox` (`desktop` = LTR/RTL; `mobile` = compact). */
export type FlywaysMapCameraVariant = 'desktop' | 'mobile';

/**
 * Compact experience matches Tailwind `lg` (64rem): mobile chrome below,
 * desktop filter overlay at `width >= 64rem`.
 */
export const FLYWAYS_MAP_COMPACT_MEDIA_QUERY = '(width < 64rem)';

export const FLYWAYS_MAP_FILTERS = [
  'all',
  'americas',
  'african-eurasian',
  'central-asia',
  'east-asia-australasia',
  'marine',
] as const satisfies readonly FlywayId[];

export type FlywaysMapFilterId = (typeof FLYWAYS_MAP_FILTERS)[number];

export const FLYWAYS_MAP_DEFAULT_FILTER = 'all' satisfies FlywaysMapFilterId;

export const FLYWAYS_MAP_MARINE_FILTER = 'marine' satisfies FlywaysMapFilterId;

export const FLYWAYS_MAP_COLORS = {
  americas: 'var(--flyway-americas)',
  'african-eurasian': 'var(--flyway-african-eurasian)',
  'central-asia': 'var(--flyway-central-asia)',
  'east-asia-australasia': 'var(--flyway-east-asia-australasia)',
  marine: 'var(--flyway-marine)',
} as const satisfies Record<Exclude<FlywaysMapFilterId, 'all'>, string>;

/**
 * Dark ink for species-count text on flyway accent fills (map badges +
 * compact info popover header). Matches SVG `badge-*-count` fills.
 */
export const FLYWAYS_MAP_INKS = {
  americas: 'var(--flyway-americas-ink)',
  'african-eurasian': 'var(--flyway-african-eurasian-ink)',
  'central-asia': 'var(--flyway-central-asia-ink)',
  'east-asia-australasia': 'var(--flyway-east-asia-australasia-ink)',
  marine: 'var(--flyway-marine-ink)',
} as const satisfies Record<Exclude<FlywaysMapFilterId, 'all'>, string>;

/** Figma map plate aspect (1045×618) — desktop viewport chrome. */
export const FLYWAYS_MAP_ASPECT_RATIO = 1045 / 618;

export const FLYWAYS_MAP_VIEWBOX = { width: 1705, height: 1195 } as const;

/**
 * SVG artboard aspect — layout contract for the CSS camera plate.
 * {@link cameraToCssTransform} matches {@link cameraToViewBox} only when the
 * SVG’s layout box uses this ratio (not the Figma plate ratio above).
 */
export const FLYWAYS_MAP_ARTBOARD_ASPECT_RATIO =
  FLYWAYS_MAP_VIEWBOX.width / FLYWAYS_MAP_VIEWBOX.height;

export const FLYWAYS_MAP_SVG_PATH = '/svg/flyways-map.svg';

/** Decorative pan/zoom affordance on the compact map. */
export const FLYWAYS_MAP_PAN_ICON_PATH = '/svg/flyways-map-pan-icon.svg';

/**
 * Species counts from SVG badge text (`badge-{flyway}-count`).
 * Used by the compact info popover (badges are hidden on small screens).
 */
export const FLYWAYS_MAP_SPECIES_COUNTS = {
  americas: 266,
  'african-eurasian': 465,
  'central-asia': 324,
  'east-asia-australasia': 523,
  marine: 523,
} as const satisfies Record<Exclude<FlywaysMapFilterId, 'all'>, number>;

/** Maximum caption width inside a selected flyway's SVG species badge. */
export const FLYWAYS_MAP_BADGE_LABEL_MAX_WIDTH = 112;

/** Baseline spacing for wrapped flyway badge captions in SVG artboard units. */
export const FLYWAYS_MAP_BADGE_LABEL_LINE_HEIGHT = 14;

/** Baseline spacing for bilingual species common names in SVG artboard units. */
export const FLYWAYS_MAP_SPECIES_LABEL_LINE_HEIGHT = 17;

/** Minimum clear space between a bird cutout and its common-name ink. */
export const FLYWAYS_MAP_SPECIES_LABEL_BIRD_GAP = 8;

export const FLYWAYS_MAP_VIEWPORT_ROOT_MARGIN = '200px 0px';

/** Marker on trail `<g>` groups created at runtime. */
export const FLYWAYS_MAP_COMET_TRAIL_ATTR = 'data-flyways-comet-trail';

/**
 * Root `<svg>` marker set by `normalizeFlywaysMapSvgMarkup` after fetch.
 */
export const FLYWAYS_MAP_SVG_ROOT_ATTR = 'data-flyways-map';

/** Always-visible base layer. */
export const FLYWAYS_MAP_WORLD_ID = 'world-map-master';

/** Paint-order type roots under `#flyways-map-root` (world → fills → paths → birds → badges). */
export const FLYWAYS_MAP_TYPE_ROOT_IDS = [
  'fills',
  'paths',
  'birds',
  'badges',
] as const;

export type FlywaysMapTypeRootId = (typeof FLYWAYS_MAP_TYPE_ROOT_IDS)[number];

export type FlywaysMapFlywayId = Exclude<FlywaysMapFilterId, 'all'>;

const FLYWAY_IDS = [
  'americas',
  'african-eurasian',
  'central-asia',
  'east-asia-australasia',
  'marine',
] as const satisfies readonly FlywaysMapFlywayId[];

function leafId(
  flywayId: FlywaysMapFlywayId,
  state: 'default' | 'selected',
  type: FlywaysMapTypeRootId,
): string {
  return `${flywayId}-${state}-${type}`;
}

/**
 * Default leaves replaced, rather than supplemented, by selected-state artwork.
 * These leaves remain visible in All Flyways and are hidden only while their
 * flyway's selected leaves are active.
 */
export const FLYWAYS_MAP_SELECTED_STATE_HIDDEN_DEFAULT_LEAF_IDS = [
  leafId('east-asia-australasia', 'default', 'paths'),
] as const satisfies readonly string[];

/** SELECTED region-fill groups under `#fills`. */
export const FLYWAYS_MAP_FILL_GROUP_IDS = {
  americas: [leafId('americas', 'selected', 'fills')],
  'african-eurasian': [leafId('african-eurasian', 'selected', 'fills')],
  'central-asia': [leafId('central-asia', 'selected', 'fills')],
  'east-asia-australasia': [
    leafId('east-asia-australasia', 'selected', 'fills'),
  ],
  marine: [leafId('marine', 'selected', 'fills')],
} as const satisfies Record<FlywaysMapFlywayId, readonly string[]>;

/**
 * Selected-fill leaves that contain collapsed hatch pattern rects.
 * Ingest/validator ownership contract — keep in sync with
 * `FLYWAYS_MAP_HATCH_PATTERN_RECT_IDS`.
 */
export const FLYWAYS_MAP_HATCH_FILL_GROUP_IDS = [
  ...FLYWAYS_MAP_FILL_GROUP_IDS.americas,
  ...FLYWAYS_MAP_FILL_GROUP_IDS['african-eurasian'],
] as const;

/**
 * Collapsed hatch `<rect>` ids (`fill="url(#flyway-hatch-…)"` inside alpha
 * masks). Keep in sync with ingest validator hatch rect ownership.
 */
export const FLYWAYS_MAP_HATCH_PATTERN_RECT_IDS = [
  'DOTS',
  'LINES',
  'DOTS_2',
  'LINES_2',
] as const;

/** Path groups that carry migration route strokes (under `#paths`). */
export const FLYWAYS_MAP_PATH_GROUP_IDS = {
  americas: [
    leafId('americas', 'default', 'paths'),
    leafId('americas', 'selected', 'paths'),
  ],
  'african-eurasian': [
    leafId('african-eurasian', 'default', 'paths'),
    leafId('african-eurasian', 'selected', 'paths'),
  ],
  'central-asia': [
    leafId('central-asia', 'default', 'paths'),
    leafId('central-asia', 'selected', 'paths'),
  ],
  'east-asia-australasia': [
    leafId('east-asia-australasia', 'default', 'paths'),
    leafId('east-asia-australasia', 'selected', 'paths'),
  ],
  marine: [leafId('marine', 'default', 'paths')],
} as const satisfies Record<FlywaysMapFlywayId, readonly string[]>;

/** Marine artwork shifted independently of the world map in its desktop tab. */
export const FLYWAYS_MAP_MARINE_SELECTED_OFFSET_GROUP_IDS = [
  ...FLYWAYS_MAP_FILL_GROUP_IDS.marine,
  ...FLYWAYS_MAP_PATH_GROUP_IDS.marine,
] as const;

/** Bird cutout + label groups under `#birds`. */
export const FLYWAYS_MAP_BIRD_GROUP_IDS = {
  americas: [
    leafId('americas', 'default', 'birds'),
    leafId('americas', 'selected', 'birds'),
  ],
  'african-eurasian': [
    leafId('african-eurasian', 'default', 'birds'),
    leafId('african-eurasian', 'selected', 'birds'),
  ],
  'central-asia': [
    leafId('central-asia', 'default', 'birds'),
    leafId('central-asia', 'selected', 'birds'),
  ],
  'east-asia-australasia': [
    leafId('east-asia-australasia', 'default', 'birds'),
    leafId('east-asia-australasia', 'selected', 'birds'),
  ],
  marine: [
    leafId('marine', 'default', 'birds'),
    leafId('marine', 'selected', 'birds'),
  ],
} as const satisfies Record<FlywaysMapFlywayId, readonly string[]>;

/** SELECTED badge groups under `#badges`. */
export const FLYWAYS_MAP_BADGE_GROUP_IDS = {
  americas: [leafId('americas', 'selected', 'badges')],
  'african-eurasian': [leafId('african-eurasian', 'selected', 'badges')],
  'central-asia': [leafId('central-asia', 'selected', 'badges')],
  'east-asia-australasia': [
    leafId('east-asia-australasia', 'selected', 'badges'),
  ],
  marine: [leafId('marine', 'selected', 'badges')],
} as const satisfies Record<FlywaysMapFlywayId, readonly string[]>;

/** All visibility/fade leaf group ids for a flyway (default + selected). */
export function flywaysMapLeafGroupIds(
  flywayId: FlywaysMapFlywayId,
): readonly string[] {
  return [
    ...FLYWAYS_MAP_FILL_GROUP_IDS[flywayId],
    ...FLYWAYS_MAP_PATH_GROUP_IDS[flywayId],
    ...FLYWAYS_MAP_BIRD_GROUP_IDS[flywayId],
    ...FLYWAYS_MAP_BADGE_GROUP_IDS[flywayId],
  ];
}

/** Default-state leaves only (shown for filter `all` and the active flyway). */
export function flywaysMapDefaultLeafGroupIds(
  flywayId: FlywaysMapFlywayId,
): readonly string[] {
  return flywaysMapLeafGroupIds(flywayId).filter((id) =>
    id.includes('-default-'),
  );
}

/** Selected-state leaves only (shown when that flyway filter is active). */
export function flywaysMapSelectedLeafGroupIds(
  flywayId: FlywaysMapFlywayId,
): readonly string[] {
  return flywaysMapLeafGroupIds(flywayId).filter((id) =>
    id.includes('-selected-'),
  );
}

export const FLYWAYS_MAP_FLYWAY_IDS = FLYWAY_IDS;

export const FLYWAYS_MAP_SPECIES_IDS = [
  'hudsonian-godwit',
  'rufous-hummingbird',
  'swainsons-hawk',
  'pacific-swift',
  'rainbow-bee-eater',
  'far-eastern-curlew',
  'red-footed-falcon',
  'common-eider',
  'northern-wheatear',
  'indian-paradise-flycatcher',
  'sociable-lapwing',
  'bar-headed-goose',
  'grey-headed-albatross',
  'arctic-tern',
  'south-polar-skua',
] as const;

export type FlywaysMapSpeciesId = (typeof FLYWAYS_MAP_SPECIES_IDS)[number];

export type FlywaysMapCamera = FlywayMapCamera;

/** Re-export — tune cameras + placements in `@/config/flyway-map`. */
export {
  flywayMapCamera as FLYWAYS_MAP_CAMERA,
  flywayMapCameraMobile as FLYWAYS_MAP_CAMERA_MOBILE,
  flywayMapCameraMobileWide as FLYWAYS_MAP_CAMERA_MOBILE_WIDE,
  flywayMapCameraMobileNarrowWidth as FLYWAYS_MAP_CAMERA_MOBILE_NARROW_WIDTH,
  flywayMapCameraMobileWideWidth as FLYWAYS_MAP_CAMERA_MOBILE_WIDE_WIDTH,
  flywayMapCameraRtl as FLYWAYS_MAP_CAMERA_RTL,
  flywayMapGesture as FLYWAYS_MAP_GESTURE,
  flywayMapPlacements as FLYWAYS_MAP_PLACEMENTS,
} from '@/config/flyway-map';

/**
 * Convert a camera (scale + fractional pan, origin at centre) into an SVG
 * viewBox string matching the previous CSS transform choreography.
 * Desktop: `flywayMapCameraRtl` when `dir` is `'rtl'`, else LTR.
 * Mobile: lerps narrow→wide compact tables by `containerWidth` (omit → narrow).
 */
export function cameraToViewBox(
  filter: FlywaysMapFilterId,
  dir: FlywaysMapDir = 'ltr',
  variant: FlywaysMapCameraVariant = 'desktop',
  containerWidth?: number,
): string {
  const { viewX, viewY, viewWidth, viewHeight } = resolveFlywaysMapCamera(
    filter,
    dir,
    variant,
    containerWidth,
  );
  return `${viewX} ${viewY} ${viewWidth} ${viewHeight}`;
}

export type FlywaysMapCssCamera = {
  /** GSAP/CSS scale (≥ 1). */
  scale: number;
  /** Percent of element width (transform-origin top-left). */
  xPercent: number;
  /** Percent of element height (transform-origin top-left). */
  yPercent: number;
};

/**
 * CSS transform that matches {@link cameraToViewBox} when:
 * - the SVG viewBox is the full artboard (`FLYWAYS_MAP_FULL_VIEWBOX`)
 * - GSAP targets the artboard-aspect cover `.plate` (not the viewport `.camera`)
 * - that plate uses `transform-origin: 0 0`
 * - xPercent / yPercent are percentages of the **plate** size
 *
 * Pan must scale with zoom: translate by `-view / viewSize`, not `-view / mapSize`
 * (equivalently multiply the full-artboard fraction by `scale`).
 */
export function cameraToCssTransform(
  filter: FlywaysMapFilterId,
  dir: FlywaysMapDir = 'ltr',
  variant: FlywaysMapCameraVariant = 'desktop',
  containerWidth?: number,
): FlywaysMapCssCamera {
  const { viewX, viewY, viewWidth, viewHeight, scale } = resolveFlywaysMapCamera(
    filter,
    dir,
    variant,
    containerWidth,
  );
  return {
    scale,
    xPercent: (-viewX / viewWidth) * 100,
    yPercent: (-viewY / viewHeight) * 100,
  };
}

/** Full-artboard viewBox — pair with {@link cameraToCssTransform} during tweens. */
export const FLYWAYS_MAP_FULL_VIEWBOX = `0 0 ${FLYWAYS_MAP_VIEWBOX.width} ${FLYWAYS_MAP_VIEWBOX.height}`;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function resolveMobileCamera(
  filter: FlywaysMapFilterId,
  containerWidth?: number,
): FlywayMapCamera {
  const narrow = flywayMapCameraMobile[filter];
  if (containerWidth == null) return narrow;

  const span =
    flywayMapCameraMobileWideWidth - flywayMapCameraMobileNarrowWidth;
  const t = Math.min(
    1,
    Math.max(0, (containerWidth - flywayMapCameraMobileNarrowWidth) / span),
  );
  if (t <= 0) return narrow;
  if (t >= 1) return flywayMapCameraMobileWide[filter];

  const wide = flywayMapCameraMobileWide[filter];
  return {
    x: lerp(narrow.x, wide.x, t),
    y: lerp(narrow.y, wide.y, t),
    scale: lerp(narrow.scale, wide.scale, t),
  };
}

function resolveFlywaysMapCamera(
  filter: FlywaysMapFilterId,
  dir: FlywaysMapDir,
  variant: FlywaysMapCameraVariant,
  containerWidth?: number,
): {
  scale: number;
  viewX: number;
  viewY: number;
  viewWidth: number;
  viewHeight: number;
} {
  const { width: mapWidth, height: mapHeight } = FLYWAYS_MAP_VIEWBOX;
  const camera =
    variant === 'mobile'
      ? resolveMobileCamera(filter, containerWidth)
      : dir === 'rtl'
        ? flywayMapCameraRtl[filter]
        : flywayMapCamera[filter];
  const { x, y, scale } = camera;
  const viewWidth = mapWidth / scale;
  const viewHeight = mapHeight / scale;
  const viewX = mapWidth / 2 - viewWidth / 2 - x * viewWidth;
  const viewY = mapHeight / 2 - viewHeight / 2 - y * viewHeight;
  return { scale, viewX, viewY, viewWidth, viewHeight };
}

/** Re-export — tune timings in `@/config/flyway-map`. */
export { flywayMapMotion as FLYWAYS_MAP_MOTION } from '@/config/flyway-map';

export type FlywaysMapLegendPattern =
  | 'solid'
  | 'stripes'
  | 'dots'
  | 'rings'
  | 'slashes'
  | 'crosses';

export type FlywaysMapLegendLabelKey =
  | 'pacificAmericas'
  | 'centralAmericas'
  | 'atlanticAmericas'
  | 'eastAtlantic'
  | 'blackSeaMediterranean'
  | 'eastAsiaEastAfrica'
  | 'centralAsian'
  | 'eastAsianAustralasian'
  | 'pacificOcean'
  | 'atlanticOcean'
  | 'northIndianOcean'
  | 'eastIndianOcean'
  | 'westPacificOcean'
  | 'southernOcean';

export type FlywaysMapLegendItem = {
  id: string;
  labelKey: FlywaysMapLegendLabelKey;
  pattern: FlywaysMapLegendPattern;
};

/** Legend-only sub-flyway keys (messages.legendItems). */
export const FLYWAYS_MAP_LEGEND = {
  americas: [
    { id: 'pacific-americas', labelKey: 'pacificAmericas', pattern: 'stripes' },
    { id: 'central-americas', labelKey: 'centralAmericas', pattern: 'solid' },
    { id: 'atlantic-americas', labelKey: 'atlanticAmericas', pattern: 'dots' },
  ],
  'african-eurasian': [
    { id: 'east-atlantic', labelKey: 'eastAtlantic', pattern: 'stripes' },
    {
      id: 'black-sea-mediterranean',
      labelKey: 'blackSeaMediterranean',
      pattern: 'solid',
    },
    {
      id: 'east-asia-east-africa',
      labelKey: 'eastAsiaEastAfrica',
      pattern: 'dots',
    },
  ],
  'central-asia': [
    { id: 'central-asian', labelKey: 'centralAsian', pattern: 'solid' },
  ],
  'east-asia-australasia': [
    {
      id: 'east-asian-australasian',
      labelKey: 'eastAsianAustralasian',
      pattern: 'solid',
    },
  ],
  marine: [
    { id: 'pacific-ocean', labelKey: 'pacificOcean', pattern: 'dots' },
    { id: 'atlantic-ocean', labelKey: 'atlanticOcean', pattern: 'stripes' },
    {
      id: 'north-indian-ocean',
      labelKey: 'northIndianOcean',
      pattern: 'rings',
    },
    {
      id: 'east-indian-ocean',
      labelKey: 'eastIndianOcean',
      pattern: 'slashes',
    },
    {
      id: 'west-pacific-ocean',
      labelKey: 'westPacificOcean',
      pattern: 'crosses',
    },
    { id: 'southern-ocean', labelKey: 'southernOcean', pattern: 'solid' },
  ],
} as const satisfies Record<
  Exclude<FlywaysMapFilterId, 'all'>,
  readonly FlywaysMapLegendItem[]
>;

export function flywaysMapLabelTextId(speciesId: FlywaysMapSpeciesId): string {
  return `label-${speciesId}-common`;
}

export function flywaysMapBadgeCountId(
  flywayId: Exclude<FlywaysMapFilterId, 'all'>,
): string {
  return `badge-${flywayId}-count`;
}

export function flywaysMapBadgeLabelId(
  flywayId: Exclude<FlywaysMapFilterId, 'all'>,
): string {
  return `badge-${flywayId}-label`;
}
